'use client';

import * as React from 'react';
import { styled } from '@linaria/react';

import { HEADER_HEIGHT, SUPERHEADER_HEIGHT } from '@/constants';

import { useGlobalUIState } from '@/components/GlobalUIProvider';
import GlassyBackdrop from '@/components/GlassyBackdrop';

function GlassyHeaderBackdrop({
  children,
}: {
  children?: React.ReactNode;
}) {
  const { superheader } = useGlobalUIState();

  const [opacity, setOpacity] = React.useState(1);
  const ref = React.useRef<HTMLDivElement>(null);

  const hasMounted = React.useRef(false);

  React.useEffect(() => {
    hasMounted.current = true;
  }, []);

  React.useEffect(() => {
    const target = ref.current; // Element to observe
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.intersectionRatio < 1) {
          setOpacity(1);
        } else {
          setOpacity(0);
        }
      },
      {
        root: null,
        threshold: [0, 1.0],
        // Technically, the header never crosses the top of the viewport, since it sticks at 0px, so we'll pretend that it's 1px higher up than it is.
        rootMargin: '-1px 0px 0px 0px',
      }
    );

    if (target) {
      observer.observe(target);
    }

    // Cleanup on component unmount
    return () => {
      if (target) {
        observer.unobserve(target);
      }
    };
  }, []);

  const blockerHeight = superheader
    ? HEADER_HEIGHT + SUPERHEADER_HEIGHT
    : HEADER_HEIGHT;

  return (
    <Wrapper
      ref={ref}
      backgroundColor="var(--color-background)"
      glassColor="var(--color-background)"
      opacity={opacity}
      style={{ '--height': blockerHeight + 'rem' }}
    >
      {children}
    </Wrapper>
  );
}

const Wrapper = styled(GlassyBackdrop)`
  && {
    position: sticky;
    top: 0;
    height: var(--height);
    z-index: 2;
  }
`;

export default GlassyHeaderBackdrop;
